import { MARKUP_KEY_BY_MATRIX, MATRIX_LABEL_BY_KEY } from '@crm/shared'
import type { RequestItem } from '../../../shared/api/types'
import type { SelectedPrice } from '../hooks/useKpMode'
import type { QuotesSummaryPosition } from '../../../shared/api/quotesSelection.api'
import type { MarkupConstants } from '../../../shared/api/exchangeRates.api'

/**
 * Предзаполнение выбора цен для режима КП на основе квот, которые менеджер
 * уже выбрал и сохранил на странице «Выбор квот».
 *
 * Источник правды — `selectedQuoteId` из сводки `quotes-summary` (бэк считает
 * его той же логикой, что подсвечивает выбранную квоту зелёным на странице
 * «Выбор квот»). Для каждой позиции с выбранной квотой определяем колонку и
 * матрицу по её `sourceType` и считаем себестоимость в ₽ ровно так же, как
 * при ручном клике по ячейке в таблице (см. priceColDefs.toRub) — чтобы
 * подсветка легла на ту же ячейку с тем же значением.
 *
 * Позиции, где менеджер квоту не выбирал (`selectedQuoteId === null`), в карту
 * не попадают — их ячейки останутся неподсвеченными, как и просил сценарий.
 */

// sourceType квоты → colId ячейки в priceColDefs, ценовое поле позиции и
// ключ матрицы (для наценки и подписи). Совпадает с SOURCE_TO_FIELD на бэке
// (quotes-selection.service.ts) и с makePriceCol в priceColDefs.tsx.
export const SOURCE_INFO: Record<
  string,
  {
    col: string
    field: 'priceStock' | 'targetPrice' | 'priceRF' | 'priceManufacturer' | 'priceOther'
    matrix: string
  }
> = {
  STOCK: { col: 'priceStock', field: 'priceStock', matrix: 'stock' },
  CHINA: { col: 'price', field: 'targetPrice', matrix: 'china' },
  RF: { col: 'priceRF', field: 'priceRF', matrix: 'rf' },
  MANUFACTURER: { col: 'priceManufacturer', field: 'priceManufacturer', matrix: 'manuf' },
  OTHER: { col: 'priceOther', field: 'priceOther', matrix: 'other' },
}

// Запасная подпись поставщика, если в квоте имя пустое (общий словарь с
// priceColDefs/computeBestPrice — @crm/shared).
const MATRIX_LBL = MATRIX_LABEL_BY_KEY

// Себестоимость в ₽ — идентично priceColDefs.toRub: для РФ цена уже в рублях,
// остальные матрицы хранятся в юанях и переводятся по курсу CNY.
// Коэффициента матрицы нет в справочнике → null: позиция в предзаполнение не
// попадёт. Зашитого коэффициента «по умолчанию» здесь нет — иначе цена
// считалась бы по наценке, которой нет ни в одном справочнике.
function toRub(raw: number, matrix: string, cnyRate: number, mc: MarkupConstants): number | null {
  const markupKey = MARKUP_KEY_BY_MATRIX[matrix]
  const k = markupKey ? mc[markupKey] : undefined
  if (k == null) return null
  if (matrix === 'rf') return raw * k
  return raw * k * cnyRate
}

/** Курс CNY и коэффициенты наценки обязательны — вызов возможен только после
 *  гейта готовности цен (см. useRequestRelatedData.pricing / useEnterKpMode). */
export function buildKpPrefillFromSelection(params: {
  positions: QuotesSummaryPosition[]
  items: RequestItem[]
  cnyRate: number
  markupConstants: MarkupConstants
}): Map<string, SelectedPrice> {
  const { positions, items, cnyRate, markupConstants } = params
  const itemById = new Map(items.map((i) => [i.id, i]))
  const result = new Map<string, SelectedPrice>()

  for (const pos of positions) {
    if (!pos.selectedQuoteId) continue
    const item = itemById.get(pos.id)
    if (!item) continue // позиция не во видимом наборе — пропускаем
    const quote = pos.quotes.find((q) => q.id === pos.selectedQuoteId)
    if (!quote) continue
    const info = SOURCE_INFO[quote.sourceType]
    if (!info) continue

    const raw = item[info.field]
    if (raw == null) continue // поле позиции пустое — подсвечивать нечего

    const cost = toRub(raw, info.matrix, cnyRate, markupConstants)
    if (cost == null) continue // нет коэффициента наценки для матрицы — пропуск
    result.set(pos.id, {
      col: info.col,
      matrix: info.matrix,
      cost,
      supplierName: quote.supplierName || MATRIX_LBL[info.matrix] || null,
    })
  }

  return result
}
